<template>
    <TransitionRoot :show="open" as="template" appear @after-leave="query = ''">
        <Dialog as="div" class="relative z-50" @close="$emit('close')">
            <TransitionChild
                as="template"
                enter="ease-out duration-150"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-100"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div
                    class="fixed inset-0 bg-[#0C0A14]/60 backdrop-blur-sm"
                ></div>
            </TransitionChild>

            <div class="fixed inset-0 z-50 overflow-y-auto p-4 sm:p-6 md:p-20">
                <TransitionChild
                    as="template"
                    enter="ease-out duration-150"
                    enter-from="opacity-0 scale-95"
                    enter-to="opacity-100 scale-100"
                    leave="ease-in duration-100"
                    leave-from="opacity-100 scale-100"
                    leave-to="opacity-0 scale-95"
                >
                    <DialogPanel
                        class="mx-auto max-w-xl transform overflow-hidden rounded-2xl border border-black/5 bg-white shadow-2xl transition-all dark:border-white/10 dark:bg-[#131022]"
                    >
                        <Combobox
                            v-slot="{ activeOption }"
                            @update:model-value="onSelect"
                        >
                            <div class="relative">
                                <MagnifyingGlassIcon
                                    class="pointer-events-none absolute left-4 top-3.5 h-5 w-5 text-gray-400"
                                    aria-hidden="true"
                                />
                                <ComboboxInput
                                    class="h-12 w-full border-0 bg-transparent pl-11 pr-4 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-hidden focus:ring-0 dark:text-white"
                                    placeholder="Tela, seller (nome ou e-mail), ID de pedido..."
                                    @change="query = $event.target.value"
                                    @keydown.enter="
                                        onEnterFallback(activeOption)
                                    "
                                />
                            </div>

                            <ComboboxOptions
                                v-if="results.length > 0"
                                static
                                class="max-h-80 scroll-py-2 overflow-y-auto border-t border-black/5 py-2 dark:border-white/10"
                            >
                                <ComboboxOption
                                    v-for="result in results"
                                    :key="result.key"
                                    v-slot="{ active }"
                                    :value="result"
                                    as="template"
                                >
                                    <li
                                        class="flex cursor-pointer items-center gap-3 px-4 py-2.5 text-sm"
                                        :class="
                                            active
                                                ? 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-300'
                                                : 'text-gray-700 dark:text-gray-200'
                                        "
                                    >
                                        <component
                                            :is="result.icon"
                                            v-if="result.icon"
                                            class="h-4 w-4 shrink-0 opacity-60"
                                            aria-hidden="true"
                                        />
                                        <span class="flex-1 truncate">
                                            {{ result.label }}
                                        </span>
                                        <span
                                            v-if="result.hint"
                                            class="font-mono text-[11px] text-gray-400"
                                        >
                                            {{ result.hint }}
                                        </span>
                                    </li>
                                </ComboboxOption>
                            </ComboboxOptions>

                            <p
                                v-if="lookupError || resolvingHash"
                                class="border-t border-black/5 px-4 py-3 text-center text-sm dark:border-white/10"
                                :class="
                                    lookupError
                                        ? 'text-red-500 dark:text-red-400'
                                        : 'text-gray-400'
                                "
                            >
                                {{ lookupError ?? "Abrindo pedido..." }}
                            </p>

                            <p
                                v-else-if="results.length === 0"
                                class="border-t border-black/5 px-4 py-6 text-center text-sm text-gray-400 dark:border-white/10"
                            >
                                {{
                                    searchingUsers
                                        ? "Buscando sellers..."
                                        : "Nada encontrado. Busque por nome, e-mail ou digite um ID numérico."
                                }}
                            </p>
                        </Combobox>
                    </DialogPanel>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script>
import {
    Combobox,
    ComboboxInput,
    ComboboxOption,
    ComboboxOptions,
    Dialog,
    DialogPanel,
    TransitionChild,
    TransitionRoot,
} from "@headlessui/vue";
import {
    CreditCardIcon,
    MagnifyingGlassIcon,
    ShoppingCartIcon,
    UserIcon,
} from "@heroicons/vue/24/outline";

export default {
    name: "CommandPalette",

    components: {
        Combobox,
        ComboboxInput,
        ComboboxOption,
        ComboboxOptions,
        Dialog,
        DialogPanel,
        TransitionChild,
        TransitionRoot,
        MagnifyingGlassIcon,
        CreditCardIcon,
        ShoppingCartIcon,
        UserIcon,
    },

    props: {
        open: {
            type: Boolean,
            default: false,
        },
        navigation: {
            type: Array,
            default: () => [],
        },
    },

    emits: ["close"],

    data() {
        return {
            query: "",
            userResults: [],
            searchingUsers: false,
            searchTimer: null,
            searchAbort: null,
            lastSearchedTerm: null,
            resolvingHash: false,
            lookupError: null,
        };
    },

    computed: {
        results() {
            const term = this.query.trim().toLowerCase();
            const upper = this.query.trim().toUpperCase();

            if (/^TO[PCB][0-9A-Z]{6,}$/.test(upper)) {
                return [
                    {
                        key: `order-hash-${upper}`,
                        label: `Abrir pedido ${upper}`,
                        icon: ShoppingCartIcon,
                        hashLookup: {
                            param: "filter[orderHash]",
                            value: upper,
                        },
                    },
                ];
            }

            if (/^TP[PCB][0-9A-Z]{6,}$/.test(upper)) {
                return [
                    {
                        key: `transaction-hash-${upper}`,
                        label: `Abrir pedido da transação ${upper}`,
                        icon: CreditCardIcon,
                        hashLookup: {
                            param: "filter[transactionHash]",
                            value: upper,
                        },
                    },
                ];
            }

            if (/^\d{2,}$/.test(term)) {
                return [
                    {
                        key: `seller-${term}`,
                        label: `Abrir auditoria do seller ${term}`,
                        icon: UserIcon,
                        path: `/user/${term}/audit/metrics`,
                    },
                    {
                        key: `order-${term}`,
                        label: `Abrir pedido ${term}`,
                        icon: ShoppingCartIcon,
                        path: `/order/${term}`,
                    },
                ];
            }

            const screens = this.navigation.map((item) => ({
                key: `nav-${item.name}`,
                label: item.name,
                icon: item.icon,
                route: item.route,
            }));

            if (term === "") {
                return screens.slice(0, 8);
            }

            const screenMatches = screens
                .filter((item) => item.label.toLowerCase().includes(term))
                .slice(0, 4);

            return [...screenMatches, ...this.userResults];
        },
    },

    watch: {
        open(value) {
            if (!value) {
                this.query = "";
                this.userResults = [];
                this.lookupError = null;
            }
        },

        query(term) {
            clearTimeout(this.searchTimer);
            this.lookupError = null;

            const trimmed = term.trim();

            if (trimmed.length < 3 || /^\d+$/.test(trimmed)) {
                this.searchAbort?.abort();
                this.userResults = [];
                this.lastSearchedTerm = null;
                return;
            }

            if (trimmed === this.lastSearchedTerm) {
                return;
            }

            this.searchTimer = setTimeout(() => this.searchUsers(trimmed), 400);
        },
    },

    beforeUnmount() {
        clearTimeout(this.searchTimer);
        this.searchAbort?.abort();
    },

    methods: {
        async searchUsers(term) {
            this.searchAbort?.abort();
            this.searchAbort = new AbortController();
            this.searchingUsers = true;
            this.lastSearchedTerm = term;

            try {
                const { data } = await axios.get("/api/user", {
                    params: { "filter[text]": term, page: 1 },
                    signal: this.searchAbort.signal,
                });

                if (term !== this.query.trim()) {
                    return;
                }

                this.userResults = (Array.isArray(data) ? data : [])
                    .slice(0, 5)
                    .map((user) => ({
                        key: `user-${user.id}`,
                        label: user.name ?? user.email,
                        hint: user.email,
                        icon: UserIcon,
                        route: {
                            name: "user.commissions",
                            params: { user_id: user.id },
                        },
                    }));
            } catch (error) {
                if (error.code !== "ERR_CANCELED") {
                    this.userResults = [];
                    this.lastSearchedTerm = null;
                }
            } finally {
                this.searchingUsers = false;
            }
        },

        /**
         * A Headless UI só trata Enter quando uma opção está ativa
         * (navegada por seta). Sem opção ativa, Enter abre o primeiro
         * resultado — o comportamento que todo mundo espera de um ⌘K.
         */
        onEnterFallback(activeOption) {
            if (activeOption || this.results.length === 0) {
                return;
            }

            this.onSelect(this.results[0]);
        },

        async onSelect(result) {
            if (!result) {
                return;
            }

            if (result.hashLookup) {
                await this.openByHash(result.hashLookup);
                return;
            }

            if (result.path) {
                this.$router.push(result.path);
            } else if (result.route) {
                this.$router.push(result.route);
            }

            this.$emit("close");
        },

        async openByHash({ param, value }) {
            this.resolvingHash = true;
            this.lookupError = null;

            try {
                const { data } = await axios.get("/api/order", {
                    params: { [param]: value, page: 1 },
                });

                const order = data?.data?.[0];

                if (!order?.id) {
                    this.lookupError = `Nenhum pedido encontrado para ${value}.`;
                    return;
                }

                this.$router.push(`/order/${order.id}`);
                this.$emit("close");
            } catch (error) {
                this.lookupError = `Falha ao buscar o pedido ${value}.`;
            } finally {
                this.resolvingHash = false;
            }
        },
    },
};
</script>
